import { useEffect, useMemo, useState } from "react";
import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { fetchCachedAsset } from "@/shared/api/cached-asset-fetch";

export interface CachedImageAsset {
  url: string;
  width: number;
  height: number;
}

export interface CachedImageSource {
  blob: Blob;
  width: number;
  height: number;
}

export interface CachedImageQueryOptions {
  /**
   * Stable logical Cache Storage/query identity to use instead of `url`.
   * Pass this for assets whose network address is a temporary signed URL
   * (e.g. a floorplan image), so a re-signed URL still hits the same
   * persistent cache entry and the same TanStack Query cache entry.
   */
  cacheKey?: string;
  /** Overrides the derived TanStack Query key entirely (e.g. to match a caller's own key shape). */
  queryKey?: readonly unknown[];
}

/**
 * Builds the shared query definition (key, fetch/decode function, and
 * enablement) for fetching an image file resource (PNG/JPEG/SVG) through the
 * cache-first `fetchCachedAsset` pipeline and decoding its native pixel
 * dimensions.
 *
 * This is the single reusable query definition shared by `useCachedImageAsset`
 * (which additionally owns a mounted consumer's object URL) and background
 * preloaders that only need the decoded Blob/dimensions to land in the query
 * cache (and, transitively, persistent Cache Storage) without ever creating
 * an object URL themselves.
 */
export function cachedImageQueryOptions(
  deploymentId: string,
  url: string | undefined,
  options: CachedImageQueryOptions = {}
): UseQueryOptions<CachedImageSource> {
  const cacheKey = options.cacheKey;

  return {
    queryKey: options.queryKey ?? ["cached-asset", deploymentId, "image", cacheKey ?? url],
    queryFn: async (): Promise<CachedImageSource> => {
      const { response } = cacheKey
        ? await fetchCachedAsset(deploymentId, url as string, { cacheKey })
        : await fetchCachedAsset(deploymentId, url as string);
      if (!response.ok) {
        throw new Error(`Failed to fetch image asset "${url}" (status ${response.status}).`);
      }
      const blob = await response.blob();
      const bitmap = await createImageBitmap(blob);
      try {
        return {
          blob,
          width: bitmap.width,
          height: bitmap.height,
        };
      } finally {
        bitmap.close();
      }
    },
    enabled: url != null,
    staleTime: Infinity,
  };
}

/**
 * Fetches an image file resource (PNG/JPEG/SVG) through the cache-first
 * `fetchCachedAsset` pipeline and exposes its native dimensions with an object
 * URL suitable for an `<img>` `src` or an SVG `<image>` `href`. The query cache
 * owns the Blob, while each mounted consumer owns and revokes its object URL.
 *
 * Pass `options.cacheKey` for assets addressed by a temporary signed URL (see
 * `cachedImageQueryOptions`), so this consumer shares its query/persistent
 * cache entry with any background preload of the same underlying asset.
 */
export function useCachedImageAsset(
  deploymentId: string,
  url: string | undefined,
  options: CachedImageQueryOptions = {}
) {
  const [objectUrl, setObjectUrl] = useState<{
    source: CachedImageSource;
    value: string;
  } | null>(null);
  const query = useQuery(cachedImageQueryOptions(deploymentId, url, options));

  useEffect(() => {
    if (!query.data) {
      setObjectUrl(null);
      return;
    }

    const value = URL.createObjectURL(query.data.blob);
    setObjectUrl({ source: query.data, value });

    return () => URL.revokeObjectURL(value);
  }, [query.data]);

  const data = useMemo(
    () =>
      query.data && objectUrl?.source === query.data
        ? {
            url: objectUrl.value,
            width: query.data.width,
            height: query.data.height,
          }
        : undefined,
    [query.data, objectUrl]
  );

  return {
    data,
    isLoading: query.isLoading || (query.data != null && data == null),
    error: query.error,
    refetch: query.refetch,
  };
}
